/**
 * Groups items from an iterable by a specified key selector function.
 *
 * @template T - The type of items being grouped
 * @template K - The type of the grouping key
 * @param items - The iterable of items to group
 * @param keySelector - Function that extracts the grouping key from each item
 * @returns A Map where keys are the group identifiers and values are arrays of grouped items
 *
 * @example
 * // Group users by role
 * const users = [
 *   { name: 'Alice', role: 'admin' },
 *   { name: 'Bob', role: 'user' },
 *   { name: 'Carol', role: 'admin' }
 * ];
 * const grouped = groupBy(users, user => user.role);
 * // Result: Map {
 * //   'admin' => [{ name: 'Alice', role: 'admin' }, { name: 'Carol', role: 'admin' }],
 * //   'user' => [{ name: 'Bob', role: 'user' }]
 * // }
 *
 * @example
 * // Group numbers by even/odd
 * const numbers = [1, 2, 3, 4, 5, 6];
 * const evenOdd = groupBy(numbers, n => n % 2 === 0 ? 'even' : 'odd');
 * // Result: Map {
 * //   'odd' => [1, 3, 5],
 * //   'even' => [2, 4, 6]
 * // }
 *
 * @example
 * // Group workers by department ID
 * interface Worker {
 *   id: number;
 *   name: string;
 *   departmentId: number;
 * }
 * const workers: Worker[] = [
 *   { id: 1, name: 'John', departmentId: 10 },
 *   { id: 2, name: 'Jane', departmentId: 20 },
 *   { id: 3, name: 'Bob', departmentId: 10 }
 * ];
 * const byDepartment = groupBy(workers, w => w.departmentId);
 * console.log(byDepartment.get(10)); // [{ id: 1, name: 'John', departmentId: 10 }, { id: 3, name: 'Bob', departmentId: 10 }]
 */
export function groupBy<T, K>(
  items: Iterable<T>,
  keySelector: (item: T) => K
): Map<K, T[]> {
  const map = new Map<K, T[]>();
  for (const item of items) {
    const key = keySelector(item);
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return map;
}

/**
 * Compares two arrays for equality by checking if they have the same length and identical elements in the same order.
 *
 * @template T - The type of elements in the arrays
 * @param arr1 - The first array to compare
 * @param arr2 - The second array to compare
 * @returns `true` if both arrays are equal, `false` otherwise
 *
 * @example
 * // Comparing two equal arrays
 * const arrayA = [1, 2, 3];
 * const arrayB = [1, 2, 3];
 * console.log(arrayIsEqual(arrayA, arrayB)); // true
 *
 * @example
 * // Comparing two different arrays
 * const arrayC = [1, 2, 3];
 * const arrayD = [1, 2, 4];
 * console.log(arrayIsEqual(arrayC, arrayD)); // false
 *
 * @example
 * // Comparing arrays of different lengths
 * const arrayE = [1, 2, 3];
 * const arrayF = [1, 2, 3, 4];
 * console.log(arrayIsEqual(arrayE, arrayF)); // false
 *
 * @example
 * // Comparing arrays with different element types
 * const arrayG = ['a', 'b', 'c'];
 * const arrayH = ['a', 'b', 'c'];
 * console.log(arrayIsEqual(arrayG, arrayH)); // true
 */
export function arrayIsEqual<T>(
  arr1: T[] | null | undefined,
  arr2: T[] | null | undefined
): boolean {
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
}
